// SPDX-License-Identifier: GPL-3.0-or-later
//
// One-time linear "Getting started" wizard. Renders the current step from
// journey state, auto-advancing as real conditions are met (the app re-renders
// on every store/wallet/extension change). Back/Skip/Dismiss repaint locally.

import {
  buildSteps,
  currentStepIndex,
  dismissJourney,
  getManualIndex,
  isJourneyActive,
  setManualIndex,
  skipStep,
  type JourneyInputs,
  type JourneyStep,
} from '../onboarding/journey';
import type { Application } from '../types';
import { EXTENSION_STORE_URL, INJINARY_STORE_URL } from './Onboarding';
import { el } from './dom';

interface StepContent {
  blurb: string;
  cheer: string;
  cta?: HTMLElement | null;
}

export function renderJourneyWizard(
  inputs: JourneyInputs,
  apps: Application[],
): HTMLElement | null {
  if (!isJourneyActive(inputs)) return null;

  const container = el('div', { class: 'journey-wizard' });

  const paint = (): void => {
    const steps = buildSteps(inputs);
    if (currentStepIndex(steps) === -1) {
      container.remove();
      return;
    }

    const total = steps.length;
    const manual = getManualIndex();
    let idx = manual !== null ? manual : currentStepIndex(steps);
    idx = Math.max(0, Math.min(idx, total - 1));
    const step = steps[idx]!;
    const doneCount = steps.filter((s) => s.done).length;
    const content = stepContent(step, inputs, apps);

    const back = el(
      'button',
      {
        class: 'btn tiny',
        disabled: idx === 0 ? true : null,
        onclick: () => {
          setManualIndex(Math.max(0, idx - 1));
          paint();
        },
      },
      '← Back',
    );

    let forward: HTMLElement | null;
    if (step.done) {
      forward = el(
        'button',
        {
          class: 'btn tiny',
          onclick: () => {
            setManualIndex(idx < total - 1 ? idx + 1 : null);
            paint();
          },
        },
        idx < total - 1 ? 'Next →' : 'Done',
      );
    } else if (step.skippable) {
      const skipLabel = step.id === 'extension' ? 'Already installed →' : 'Skip';
      forward = el(
        'button',
        {
          class: 'btn tiny',
          onclick: () => {
            skipStep(step.id);
            setManualIndex(null);
            paint();
          },
        },
        skipLabel,
      );
    } else {
      forward = null;
    }

    container.replaceChildren(
      el(
        'div',
        { class: 'jw-head' },
        el('div', { class: 'eyebrow' }, `Getting started · Step ${idx + 1} of ${total}`),
        el(
          'button',
          {
            class: 'jw-dismiss',
            title: 'Hide this guide',
            onclick: () => {
              dismissJourney();
              container.remove();
            },
          },
          "I'll explore on my own",
        ),
      ),
      el(
        'div',
        { class: 'jw-progress', 'aria-hidden': 'true' },
        el('div', {
          class: 'jw-progress-fill',
          style: `width: ${Math.round((doneCount / total) * 100)}%;`,
        }),
      ),
      el(
        'div',
        { class: 'jw-body' },
        el(
          'div',
          { class: `jw-step-icon${step.done ? ' done' : ''}` },
          step.done ? '✓' : String(idx + 1),
        ),
        el(
          'div',
          { class: 'jw-step-main' },
          el('h3', {}, step.title),
          el('p', { class: 'jw-blurb' }, content.blurb),
          el('p', { class: 'jw-cheer' }, content.cheer),
          content.cta ? el('div', { class: 'jw-cta' }, content.cta) : null,
        ),
      ),
      el('div', { class: 'jw-nav' }, back, forward),
    );
  };

  paint();
  return container;
}

function hasGenerated(a: Application): boolean {
  return !!(a.generated && (a.generated.cv || a.generated.cover || a.generated.interview));
}

function link(href: string, label: string, primary = false): HTMLElement {
  return el('a', { class: `btn compact${primary ? ' primary' : ''}`, href }, label);
}

function extLink(href: string, label: string): HTMLElement {
  return el('a', { class: 'btn compact', href, target: '_blank', rel: 'noreferrer' }, label);
}

function stepContent(step: JourneyStep, inputs: JourneyInputs, apps: Application[]): StepContent {
  switch (step.id) {
    case 'folder':
      return {
        blurb: 'Applify keeps every job, CV and cover letter inside one folder on your computer.',
        cheer: 'Done — your workspace is ready. Welcome aboard!',
        cta: null,
      };
    case 'profile':
      return step.done
        ? {
            blurb: 'Your profile is saved — every generation will draw on it.',
            cheer: 'Nice work!',
            cta: link('#/settings?focus=profile', 'Edit profile'),
          }
        : {
            blurb:
              'Paste your CV and the kinds of roles you want. This powers tailored CVs and cover letters — but you can always add it later.',
            cheer: 'Two minutes, and everything stays on your computer.',
            cta: link('#/settings?focus=profile', 'Add your profile →', true),
          };
    case 'extension':
      return step.done
        ? {
            blurb: 'The Applify extension is installed and ready to capture jobs.',
            cheer: "You're all set!",
            cta: null,
          }
        : {
            blurb:
              'The browser extension adds a one-click "Save job" button on LinkedIn, Indeed, Workday, Glassdoor and more.',
            cheer: "Installed it already? Reload this page and we'll detect it.",
            cta: extLink(EXTENSION_STORE_URL, 'Install the extension →'),
          };
    case 'firstJob':
      return step.done
        ? {
            blurb: 'Your first job is saved — the hard part is over!',
            cheer: "You're on a roll 🎉",
            cta: null,
          }
        : {
            blurb:
              'Open a job posting in another tab, click the Applify extension, and hit "Save job". It lands right here.',
            cheer:
              "Pro tip: on a search page, open the job you like first — Applify grabs the one you're viewing.",
            cta: inputs.extInstalled
              ? null
              : extLink(EXTENSION_STORE_URL, 'Install the extension first →'),
          };
    case 'wallet':
      return step.done
        ? {
            blurb: 'AI is connected — you can generate documents now.',
            cheer: 'Great — last step!',
            cta: link('#/settings?focus=wallet', 'Manage AI'),
          }
        : {
            blurb:
              'Document generation uses your own AI key, held securely by the Injinary Wallet extension. Add your key, then connect it in Settings.',
            cheer: 'You stay in control of spending — keys never touch Applify.',
            cta: el(
              'div',
              { class: 'jw-cta-row' },
              extLink(INJINARY_STORE_URL, 'Install Injinary Wallet'),
              link('#/settings?focus=wallet', 'Open AI settings →', true),
            ),
          };
    case 'firstDoc': {
      const target = apps.find((a) => !hasGenerated(a)) ?? apps[0];
      return step.done
        ? {
            blurb: "You generated your first document — that's the whole loop!",
            cheer: 'Nicely done 🚀',
            cta: null,
          }
        : {
            blurb:
              'Open a saved job and click Generate to create a tailored CV or cover letter, compiled to PDF right on your machine.',
            cheer: 'The first document is the most satisfying — go for it!',
            cta: target ? link(`#/job?id=${target.id}`, 'Open a saved job →', true) : null,
          };
    }
    default:
      return { blurb: '', cheer: '', cta: null };
  }
}
